import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { API_URL } from '../utils/config';
import './ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mainImage, setMainImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const { addToCart } = useCart();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    fetchProduct();
    fetchRelatedProducts();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setSelectedColor('');
      setSelectedSize('');
      const response = await axios.get(`${API_URL}/api/products/${id}`);
      setProduct(response.data);
      setMainImage(getImageUrl(response.data.image));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Failed to load product details');
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async () => {
    try {
      // FIXED: Changed from single quotes to backticks
      const response = await axios.get(`${API_URL}/api/products`);
      const shuffled = response.data.sort(() => 0.5 - Math.random());
      setRelatedProducts(shuffled.slice(0, 4));
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  };

  // Helper function to get correct image URL
  const getImageUrl = (imagePath) => {
    if (imagePath && imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  const handleAddToCart = () => {
    if (product) {
      if (product.colors && product.colors.length > 0 && !selectedColor) {
        alert('Please select a color!');
        return;
      }
      if (['t-shirt', 'polo-shirt', 'shirt'].includes(product.category?.toLowerCase()) && product.sizes && product.sizes.length > 0 && !selectedSize) {
        alert('Please select a size!');
        return;
      }

      addToCart(product, selectedColor || null, selectedSize || null, quantity);
      alert(`${quantity} x ${product.name} added to cart!`);
    }
  };

  const handleOrderNow = () => {
    if (product) {
      if (product.colors && product.colors.length > 0 && !selectedColor) {
        alert('Please select a color!');
        return;
      }
      if (['t-shirt', 'polo-shirt', 'shirt'].includes(product.category?.toLowerCase()) && product.sizes && product.sizes.length > 0 && !selectedSize) {
        alert('Please select a size!');
        return;
      }

      // Format selected items like the cart does so Checkout can read it
      const checkoutItem = {
        ...product,
        quantity,
        selectedColor: selectedColor || null,
        selectedSize: selectedSize || null,
        cartItemId: `${product._id}-${selectedColor || ''}-${selectedSize || ''}`
      };

      navigate('/checkout', {
        state: {
          selectedItems: [checkoutItem]
        }
      });
    }
  };

  const handleRelatedProductAddToCart = (relatedProduct) => {
    addToCart(relatedProduct);
    alert(`${relatedProduct.name} added to cart!`);
  };

  const incrementQuantity = () => {
    if (product && quantity < (product.stock || 100)) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (loading) {
    return (
      <div className="loading-page">
        <div className="loading-spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="error-page">
        <h2>Product Not Found</h2>
        <p>{error || 'The product you are looking for does not exist.'}</p>
        <Link to="/shop">
          <button className="normal">Back to Shop</button>
        </Link>
      </div>
    );
  }

  // Get all product images (main + additional)
  const allImages = [product.image, ...(product.images || [])];

  return (
    <div className="product-details">
      {/* Product Details Section */}
      <section id="prodetails" className="section-p1">
        <div className="single-pro-image">
          <img 
            src={mainImage} 
            width="100%" 
            id="MainImg" 
            alt={product.name}
            onError={(e) => {
              e.target.src = '/img/products/f1.jpg';
            }}
          />
          
          {/* Small Image Gallery - Show all available images */}
          <div className="small-img-group">
            {allImages.slice(0, 4).map((image, index) => (
              <div className="small-img-col" key={index}>
                <img 
                  src={getImageUrl(image)} 
                  width="100%" 
                  className="small-img" 
                  alt={`${product.name} ${index + 1}`}
                  onClick={() => setMainImage(getImageUrl(image))}
                  onError={(e) => {
                    e.target.src = `/img/products/f${index + 1}.jpg`;
                  }}
                />
              </div>
            ))}
            {/* Fill remaining slots with placeholders if less than 4 images */}
            {allImages.length < 4 && [...Array(4 - allImages.length)].map((_, index) => (
              <div className="small-img-col" key={`placeholder-${index}`}>
                <img 
                  src={`/img/products/f${allImages.length + index + 1}.jpg`}
                  width="100%" 
                  className="small-img" 
                  alt="Additional view"
                  onClick={() => setMainImage(`/img/products/f${allImages.length + index + 1}.jpg`)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="single-pro-details">
          <h6>Home / {product.category || 'Products'}</h6>
          <h4>{product.name}</h4>
          <h2>{product.price}৳</h2>

          {/* Color Selection Option */}
          {product.colors && product.colors.length > 0 && (
            <div className="product-option-selector">
              <label>Select Color:</label>
              <div className="option-swatches">
                {product.colors.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={`option-swatch ${selectedColor === color ? 'selected' : ''}`}
                    onClick={() => setSelectedColor(color)}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size Selection Option (Clothing categories only) */}
          {['t-shirt', 'polo-shirt', 'shirt'].includes(product.category?.toLowerCase()) && product.sizes && product.sizes.length > 0 && (
            <div className="product-option-selector">
              <label>Select Size:</label>
              <div className="option-swatches">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    type="button"
                    className={`option-swatch size-swatch ${selectedSize === size ? 'selected' : ''}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Quantity Selector */}
          <div className="quantity-selector">
            <label>Quantity:</label>
            <div className="quantity-controls">
              <button onClick={decrementQuantity} className="qty-btn">-</button>
              <span className="qty-display">{quantity}</span>
              <button onClick={incrementQuantity} className="qty-btn">+</button>
            </div>
          </div>

          <div className="product-actions">
            <button 
              className="normal add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={product.stock === 0}
            >
              {product.stock === 0 ? 'Out of Stock' : 'Add To Cart'}
            </button>
            
            <button 
              className="normal order-now-btn"
              onClick={handleOrderNow}
              disabled={product.stock === 0}
              style={{
                marginLeft: '15px',
                backgroundColor: '#e67e22',
                color: '#fff',
                borderColor: '#e67e22'
              }}
            >
              {product.stock === 0 ? 'Out of Stock' : 'Order Now'}
            </button>
          </div>

          <h4>Product Details</h4>
          <span>
            {product.description || 'High-quality IoT and smart home product from Goldvines. Perfect for home automation and smart living.'}
          </span>

          <div className="product-meta">
            <p><strong>Brand:</strong> {product.brand || 'Goldvines'}</p>
            <p><strong>Category:</strong> {product.category || 'Smart Home'}</p>
            <p>
              <strong>Availability:</strong> 
              {product.stock > 0 ? (
                <span className="in-stock"> In Stock ({product.stock} units)</span>
              ) : (
                <span className="out-of-stock"> Out of Stock</span>
              )}
            </p>
            <p><strong>SKU:</strong> EH-{product._id?.slice(-6).toUpperCase()}</p>
          </div>
        </div>
      </section>

      {/* Product Description Section - Use actual description from database */}
      <section id="product-description" className="section-p1">
        <h2>Description</h2>
        <div className="description-content">
          {product.description ? (
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>
              {product.description}
            </p>
          ) : (
            <>
              <p>
                The {product.name} is a premium quality product from {product.brand || 'Goldvines'}. 
                Perfect for smart home automation and IoT projects. Features advanced 
                technology and easy integration with your existing setup.
              </p>
              
              <h3>Features:</h3>
              <ul>
                <li>High-quality construction</li>
                <li>Easy installation</li>
                <li>WiFi connectivity</li>
                <li>App control available</li>
                <li>Energy efficient</li>
                <li>1-year warranty</li>
              </ul>
            </>
          )}
        </div>
      </section>

      {/* Related Products Section */}
      <section id="product1" className="section-p1">
        <h2>Related Products</h2>
        <p>You might also like</p>
        <div className="pro-container">
          {relatedProducts.map((relatedProduct) => (
            <div key={relatedProduct._id} className="pro">
              <Link to={`/product/${relatedProduct._id}`}>
                <img 
                  src={getImageUrl(relatedProduct.image)} 
                  alt={relatedProduct.name}
                  onError={(e) => {
                    e.target.src = '/img/products/f1.jpg';
                  }}
                />
                <div className="des">
                  <span>{relatedProduct.brand || 'Goldvines'}</span>
                  <h5>{relatedProduct.name}</h5>
                  <div className="star">
                    <i className="fas fa-star"></i>
                    <i className="fas fa-star"></i>
                    <i className="fas fa-star"></i>
                    <i className="fas fa-star"></i>
                    <i className="fas fa-star"></i>
                  </div>
                  <h4>{relatedProduct.price}৳</h4>
                </div>
              </Link>
              <button 
                className="cart-btn"
                onClick={() => handleRelatedProductAddToCart(relatedProduct)}
                disabled={relatedProduct.stock === 0}
                aria-label="Add to cart"
              >
                <i className="fa fa-shopping-cart cart"></i>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="section-p1 section-m1">
        <div className="newstext">
          <h4>Sign Up For Newsletters</h4>
          <p>Get E-mail updates about our latest shop and <span>special offers.</span></p>
        </div>
        <div className="form">
          <input type="text" placeholder="Your email address" />
          <button className="normal">Sign Up</button>
        </div>
      </section>
    </div>
  );
};

export default ProductDetails;